import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../../../../../components/Dropdown';
import useFetch from '../../../../../hooks/useFetch';
import CommonService from '../../../../../services/common.service';
import { GENERIC_LOOKUP_KEYS } from '../../../../../utils/appConstant';
import { getYearOptions, getCurrentMonthCode } from '../../../../../utils/methods';

export interface MonthYearValue {
  month: string; // month code e.g. 'APR'
  year: string;  // YYYY string e.g. '2026'
}

export const getDefaultPeriod = (): MonthYearValue => ({
  month: getCurrentMonthCode(),
  year: String(new Date().getFullYear()),
});

interface MonthYearFilterProps {
  value: MonthYearValue;
  onChange: (value: MonthYearValue) => void;
}

const MonthYearFilter: React.FC<MonthYearFilterProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const { data: monthData } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '', sortBy: 'id' },
    dependency: [],
  });

  const yearOptions = getYearOptions();

  const options = useMemo(() => {
    if (!monthData || !monthData.length) return [];

    const now = new Date();
    const currentMonthIdx = now.getMonth(); // 0-11
    const currentYear = now.getFullYear();

    const items: { label: string; value: string }[] = [];

    for (let offset = -10; offset <= 10; offset++) {
      let m = currentMonthIdx + offset;
      let y = currentYear;
      while (m < 0) { m += 12; y--; }
      while (m >= 12) { m -= 12; y++; }

      const monthEntry = monthData[m] as { id: string; name: string };
      if (!monthEntry) continue;

      const yearStr = String(y);
      const yearExists = yearOptions.some((yo) => yo.value === yearStr);
      if (!yearExists) continue;

      items.push({
        value: `${monthEntry.id}-${yearStr}`,
        label: `${monthEntry.name} ${yearStr}`,
      });
    }

    return items;
  }, [monthData, yearOptions]);

  const selectedValue = value.month && value.year ? `${value.month}-${value.year}` : '';

  const handleChange = (e: any) => {
    const raw: string = Array.isArray(e.target?.value) ? e.target.value[0] : e.target?.value || '';
    const found = options.find((o) => o.value === raw);
    if (!found) return;
    const dashIdx = found.value.indexOf('-');
    const month = found.value.slice(0, dashIdx);
    const year = found.value.slice(dashIdx + 1);
    onChange({ month, year });
  };

  return (
    <div style={{ minWidth: 160, maxWidth: 200 }}>
      <Dropdown
        id="dashboardPeriod"
        name="dashboardPeriod"
        values={selectedValue ? [selectedValue] : []}
        options={options}
        allOptions={options}
        label={t('dashboard.pageTexts.filter.period')}
        noSearch
        onChange={handleChange}
      />
    </div>
  );
};

export default MonthYearFilter;
